import React, { useEffect, useState } from "react";
import { api, fmtINR, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { FileText, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { type: "pricing", title: "Pricing Report", desc: "Full price breakdown for every procedure." },
  { type: "commission", title: "Commission Report", desc: "Commission payouts and company profit." },
  { type: "profit", title: "Profit Report", desc: "Net cost, gross profit and profit margins." },
];
const COLORS = ["#2563EB", "#0D9488", "#7C3AED", "#F59E0B", "#EF4444", "#10B981", "#0EA5E9", "#EC4899", "#6366F1", "#14B8A6"];

export default function Reports() {
  const [procs, setProcs] = useState([]);
  useEffect(() => { api.get("/procedures").then((r) => setProcs(r.data)); }, []);

  const byCategory = Object.values(procs.reduce((acc, p) => {
    acc[p.category] = acc[p.category] || { category: p.category, value: 0 };
    acc[p.category].value += p.avg_price || 0;
    return acc;
  }, {}));

  const topProcs = [...procs].sort((a, b) => (b.avg_price || 0) - (a.avg_price || 0)).slice(0, 8)
    .map((p) => ({ name: p.name.length > 12 ? p.name.slice(0, 12) + "…" : p.name, price: p.avg_price }));

  const download = async (type, fmt) => {
    try {
      const res = await api.get(`/reports/export/${fmt}?type=${type}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `youngness_${type}_report.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div><h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Reports</h2><p className="text-slate-500 mt-1">Analytics and exportable reports</p></div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
          <h3 className="font-heading font-bold mb-4">Category-wise Revenue Potential</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={byCategory} dataKey="value" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtINR(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
          <h3 className="font-heading font-bold mb-4">Top Procedures by Average Price</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topProcs}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Bar dataKey="price" fill="#2563EB" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.type} className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="font-heading font-bold">{r.title}</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">{r.desc}</p>
            <div className="flex gap-2">
              <Button onClick={() => download(r.type, "pdf")} data-testid={`pdf-${r.type}`} variant="outline" className="flex-1 gap-2"><FileText className="h-4 w-4 text-red-500" /> PDF</Button>
              <Button onClick={() => download(r.type, "excel")} data-testid={`excel-${r.type}`} variant="outline" className="flex-1 gap-2"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
